import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, LayoutTemplate } from 'lucide-react';
import { useNotify } from 'react-admin';
import { deleteNcrTemplate, getAllNcrTemplates, type NcrTemplate } from '../../../services/ncrTemplateService.ts';

const NcrTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllNcrTemplates();
      setTemplates(data);
    } catch (error) {
      notify('Failed to load templates', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteNcrTemplate(id);
      notify('Template deleted successfully');
      fetchTemplates();
    } catch {
      notify('Failed to delete template', { type: 'warning' });
    }
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (t.description?.toLowerCase().includes(searchText.toLowerCase()) ?? false);
    const matchesFilter =
      filterActive === 'all' ||
      (filterActive === 'active' && t.isActive) ||
      (filterActive === 'inactive' && !t.isActive);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      {/* Header */}
      <div className="pb-3 flex-shrink-0">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">NCR Configuration</p>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">Template Management</h2>
              <p className="text-sm text-slate-500 mt-1">Create and manage NCR workflow templates.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center h-8 px-3 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                Total: {loading ? '…' : filteredTemplates.length}
              </span>
              <button
                onClick={() => navigate('/NcrTemplate/create')}
                className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
              >
                <Plus className="w-4 h-4" />
                Create Template
              </button>
            </div>
          </div>

          {/* Search + Filter */}
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search template name or description…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 pb-4 overflow-auto">
        <div className="min-h-full rounded-2xl border border-gray-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="text-sm">Loading templates…</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
              <LayoutTemplate size={48} className="opacity-30" />
              <p className="text-base font-semibold text-slate-500">
                {searchText ? `No results for "${searchText}"` : 'No templates yet'}
              </p>
              <p className="text-sm text-slate-400">
                {searchText ? 'Try a different search term.' : 'Create your first template to get started.'}
              </p>
              {!searchText && (
                <button
                  onClick={() => navigate('/NcrTemplate/create')}
                  className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold mt-2"
                >
                  <Plus className="w-4 h-4" /> Create Template
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Stages</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Properties</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Created</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTemplates.map((template) => (
                  <tr
                    key={template.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/NcrTemplate/${template.id}/view`)}
                  >
                    <td className="px-5 py-3 font-semibold text-slate-800">{template.name}</td>
                    <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{template.description || '—'}</td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                        {template.stages?.length ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                        {template.properties?.length ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {template.isActive ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Active</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">Inactive</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">
                      {template.createdAt ? new Date(template.createdAt).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => navigate(`/NcrTemplate/${template.id}/view`)}
                          className="px-2.5 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={() => navigate(`/NcrTemplate/${template.id}/edit`)}
                          className="px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => template.id != null && handleDelete(template.id)}
                          className="px-2.5 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default NcrTemplateList;
